/**
 * This is a compiled JS file.
 * Changes here could be overwritten. 
 * Contact your Shopify developers if changes need to be made.
 */

/**
 * AddToCartBar
 * 
 * Checks the <add-to-cart-bar data-state-key="{{ state_key }}"> element
 * to get the state object it should listen for changes on
 * 
 * When it hears state has been updated (`WS_stateUpdated` event dispatched) 
 * it parses the state key to update the item count and price in the UI
 * using this.setUIValues()
 * 
 * When the user clicks "Add to Basket", it checks if the items should be posted to:
 * 
 * Shopify with this.makeSparklayerRequest()
 * or Sparklayer with this.makeSparklayerRequest()
 * 
 * Can also post a `addToCartSuccess_{{variant}}` or `addToCartError_{{variant}}` event
 * which CollectionKitRow, VariantRow, ColllectionSelectionRow are listening for
 * 
 * Used on the PDP, Package (collection.kit) and trade only "Quick Order" (collection) page templates.
 */

if (!customElements.get('add-to-cart-bar')) {
  customElements.define(
    'add-to-cart-bar',
    class AddToCartBar extends HTMLElement {
      constructor() {
        super();

        document.body.addEventListener(
          'WS_stateUpdated',
          this.onStateUpdate.bind(this)
        );

        this.stateKey = this.dataset.stateKey;

        if (!this.stateKey) {
          console.log('no state key');
          return;
        }

        const { buttonEl } = this.getUIElements();

        if (buttonEl) {
          buttonEl.addEventListener('click', async (e) => {
            e.preventDefault();

            this.classList.add('loading');

            this.collectionGroup = window.WS.getStateByKey(`${this.stateKey}`);

            if (window.spark && window.sparkOptions) {
              this.makeSparklayerRequest();
            } else {
              this.makeShopifyRequests();
            }

            return;
          });
        }

        this.init();
      }

      getUIElements() {
        return {
          totalEl: this.querySelector('[data-selection-price]'),
          quantityEl: this.querySelector('[data-selection-quantity]'),
          buttonEl: this.querySelector('[data-atc-button]'),
        };
      }

      triggerCartClick() {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          cartIconBubble.addEventListener('click', (e) => {
            e.preventDefault();
          });

          cartIconBubble.click();
        }
      }

      parseSparklayerErrors(response) {
        if (Object.entries(response).length > 0) {
          Object.entries(response).map(([, value]) => {
            if (!value.sku) {
              return;
            }

            // Array of message objects
            // [{ type: "qty-unavailable" }]
            const messages =
              value.messages.length > 0
                ? value.messages.reduce((acc, curr) => {
                    return [...acc, curr.type];
                  }, [])
                : [];

            if (
              messages.includes('qty-unavailable') ||
              messages.includes('not-found')
            ) {
              document.body.dispatchEvent(
                new CustomEvent(`addToCartError_${value.sku}`, {
                  detail: { message: 'The selected quantity is unavailable' },
                })
              );
            } else {
              document.body.dispatchEvent(
                new CustomEvent(`addToCartSuccess_${value.sku}`, {
                  detail: { message: '' },
                })
              );

              window.WS.removeStateFromLocalStorageByKey(this.stateKey);
            }
          });
          this.resetUIValues();
          this.triggerCartClick();
        }
      }

      async makeSparklayerRequest() {
        if (
          this.collectionGroup &&
          Object.entries(this.collectionGroup).length > 0
        ) {
          /**
           * Send cart updates to Sparklayer using window.spark.updateCart()
           *
           * Send each product as:
           *
           * {
           *   absoluteQuantity: int // the quantity requested
           *   sku: string // the variant sku
           * }
           *
           * Response shape:
           *
           * data: {
           *   updateCart: {
           *     // cart details
           *     cart: {}
           *     // result for each product
           *     results: [{
           *       itemKey: string,
           *       messages: [{type: "qty-unavailable"}] // Message if update was not success (i.e. out of stock, more than available quantity requested)
           *       requestedQuantity: int
           *       resultantQuantity: int
           *       sku: string
           *     }]
           *   }
           * }
           */

          const products = Object.entries(this.collectionGroup).reduce(
            (acc, curr) => {
              const [, value] = curr;

              if (Number(value.quantity) > 0) {
                return [
                  ...acc,
                  {
                    absoluteQuantity: Number(value.quantity),
                    sku: value.sku,
                  },
                ];
              } else {
                return acc;
              }
            },

            []
          );

          const response = await window.spark.updateCart({
            products: [...products],
          });

          this.parseSparklayerErrors(response);

          sparklayerHelper.updateCartCount();

          window.WS.resetGlobalState({
            key: this.stateKey,
          });
        }
      }

      async makeShopifyRequests() {
        if (
          this.collectionGroup &&
          Object.entries(this.collectionGroup).length > 0
        ) {
          const requestQueue = Object.entries(this.collectionGroup).map(
            ([key, value]) => {
              // Pattern to remove any 'core_', 'extra_' from ids
              const pattern = '([a-z]*_)?';
              const cleanedKey = key.replace(new RegExp(pattern, 'gm'), '');
              return {
                id: cleanedKey,
                request: () =>
                  fetch(window.Shopify.routes.root + 'cart/add.js', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      items: [
                        {
                          id: cleanedKey,
                          quantity: value.quantity,
                        },
                      ],
                    }),
                  }),
              };
            }
          );

          const persist = this.dataset.persist !== 'false';
          let index = 0;
          let successfulRequests = 0;

          function* makeRequests(component) {
            component.classList.add('loading');

            while (index < requestQueue.length) {
              const request = requestQueue[index].request;
              yield request()
                .then((response) => response.json())
                .then((response) => {
                  // API sends back a description when request is unsuccesful
                  if (response.description) {
                    // Unable to add product
                    // (is either out of stock or inventory max already in basket)
                    document.body.dispatchEvent(
                      new CustomEvent(
                        `addToCartError_${requestQueue[index].id}`,
                        { detail: { message: response.description } }
                      )
                    );
                  } else {
                    document.body.dispatchEvent(
                      new CustomEvent(
                        `addToCartSuccess_${requestQueue[index].id}`,
                        { detail: { message: response.description } }
                      )
                    );

                    successfulRequests++;
                  }

                  // Remove the product from state to reset the UI
                  // Last request checks below will call window.WS.resetGlobalState
                  // to trigger UI errors
                  window.WS.updateState(
                    'REMOVE',
                    {
                      id: requestQueue[index].id,
                      persist,
                    },
                    component.stateKey
                  );
                })
                .then((data) => {
                  requestGenerator.next(data);
                });

              // On last request
              if (index === requestQueue.length - 1) {
                // If they was a succesful request
                if (successfulRequests > 0) {
                  const cartNotification =
                    document.querySelector('cart-drawer');

                  // Update the cart drawer
                  // Remove group from local storage after
                  cartNotification.getSections().then(() => {
                    window.WS.removeStateFromLocalStorageByKey(
                      component.stateKey
                    );
                  });
                }

                // Call global reset to ensure UI is updated
                window.WS.resetGlobalState({
                  key: component.stateKey,
                  errors: successfulRequests !== requestQueue.length,
                });

                component.classList.remove('loading');
              }

              index++;
            }
          }

          const requestGenerator = makeRequests(this);
          requestGenerator.next();
        }
      }

      setUIValues(key) {
        const productSelections = window.WS.getStateByKey(key);

        if (!productSelections) {
          return;
        }

        let totalItems = 0;

        const entries = Object.entries(productSelections).filter((entry) => {
          const [, value] = entry;

          return Number(value.quantity) > 0;
        });

        if (entries.length > 0) {
          const totalPrice = entries.reduce((acc, curr) => {
            const [, value] = curr;
            return Number(value.price * value.quantity) + Number(acc);
          }, '');

          totalItems = entries.reduce((acc, curr) => {
            const [, value] = curr;
            return Number(value.quantity) + Number(acc);
          }, '');

          const { totalEl } = this.getUIElements();

          if (totalEl) {
            totalEl.innerHTML = Shopify.formatMoney(totalPrice);
          }

          this.classList.add('active');
        } else {
          this.classList.remove('active');
        }

        const { quantityEl } = this.getUIElements();

        if (quantityEl) {
          quantityEl.innerHTML = `${entries.length} product${
            entries.length > 1 ? 's' : ''
          } ${
            totalItems > 1
              ? `(${totalItems} item${totalItems > 1 ? 's' : ''})`
              : ''
          } selected`;
        }
      }

      resetUIValues() {
        this.classList.remove('active', 'loading');

        const { quantityEl } = this.getUIElements();

        if (quantityEl) {
          quantityEl.innerHTML = `0 selected`;
        }
      }

      confirmOnExit(key) {
        const productSelections = window.WS.getStateByKey(key);

        if (!productSelections) {
          return;
        }

        const entries = Object.entries(productSelections).filter((entry) => {
          const [, value] = entry;
          return Number(value.quantity) > 0;
        });

        confirmOnExit(entries.length > 0);
      }

      init() {
        const key = this.stateKey;

        if (!key) {
          return;
        }

        this.setUIValues(key);
      }

      onStateUpdate(event) {
        const key = this.stateKey;

        if (!key) {
          return;
        }

        this.confirmOnExit(key);
        this.setUIValues(key);
      }
    }
  );
}
